import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  SimpleGrid,
  Flex,
  Avatar,
  Icon,
  useColorModeValue,
  Badge,
  Wrap,
  WrapItem,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tag,
  TagLabel,
  TagLeftIcon,
  Progress,
  Grid,
  GridItem,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import {
  FaUserAlt,
  FaIdBadge,
  FaBuilding,
  FaFile,
  FaMap,
  FaProjectDiagram,
  FaMapMarkerAlt,
  FaBriefcase,
  FaPhone,
  FaKey,
  FaCalendar,
  FaCheckCircle,
  FaTimesCircle,
  FaEnvelope,
  FaUserShield,
  FaUserCheck,
  FaUserTie,
  FaHardHat,
} from 'react-icons/fa';
import { GiOfficeChair } from 'react-icons/gi';
import { PhoneIcon, CopyIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

const capitalizeFirst = (text) => {
  if (!text || typeof text !== 'string') return 'N/A';
  return text.charAt(0).toUpperCase() + text.slice(1);
};
   
const getRoleIcon = (role) => {
  switch (role) {
    case 'admin':
      return FaUserShield;
    case 'schemeAdmin':
      return FaUserTie;
    case 'talukaUser':
      return FaMapMarkerAlt;
    case 'departmentOfficer':
      return FaUserCheck;
    case 'projectOfficer':
      return FaHardHat;
    case 'subDivisionalOfficer':
      return GiOfficeChair;
    default:
      return FaUserAlt;
  }
};

const getRoleColor = (role) => {
  switch (role) {
    case 'admin':
      return 'purple';
    case 'schemeAdmin':
      return 'blue';
    case 'talukaUser':
      return 'green';
    case 'departmentOfficer':
      return 'teal';
    case 'projectOfficer':
      return 'purple';
    case 'subDivisionalOfficer':
      return 'red';
    default:
      return 'gray';
  }
};

const formatRole = (role) => {
  switch (role) {
    case 'talukaUser':
      return 'Tehsildar';
    case 'schemeAdmin':
      return 'Office In Charge (OIC)';
    case 'admin':
      return 'Admin';
    case 'subDivisionalOfficer':
      return 'Sub Divisional Officer';
    case 'projectOfficer':
      return 'Project Officer';
    case 'departmentOfficer':
      return 'Department Officer';
    default:
      return role?.charAt(0).toUpperCase() + role?.slice(1) || 'N/A';
  }
};

const renderArrayData = (arrayData, fieldName = 'name') => {
  if (!arrayData || !Array.isArray(arrayData) || arrayData.length === 0) {
    return (
      <Badge colorScheme="gray" variant="subtle" fontSize="xs" px={3} py={1}>
        Not Assigned
      </Badge>
    );
  }

  return (
    <Wrap spacing={2}>
      {arrayData.map((item, index) => (
        <WrapItem key={item._id || index}>
          <Badge
            colorScheme="blue"
            variant="solid"
            fontSize="xs"
            px={2}
            py={1}
            borderRadius="md"
            maxW="200px"
            isTruncated
          >
            {item[fieldName]}
          </Badge>
        </WrapItem>
      ))}
    </Wrap>
  );
};

const renderDepartmentData = (department) => {
  if (!department) {
    return (
      <Badge colorScheme="gray" variant="subtle" fontSize="xs" px={3} py={1}>
        Not Assigned
      </Badge>
    );
  }

  if (Array.isArray(department)) {
    if (department.length === 0) {
      return (
        <Badge colorScheme="gray" variant="subtle" fontSize="xs" px={3} py={1}>
          Not Assigned
        </Badge>
      );
    }

    return (
      <Wrap spacing={2}>
        {department.map((dept, index) => (
          <WrapItem key={dept._id || index}>
            <Badge
              colorScheme="blue"
              variant="solid"
              fontSize="xs"
              px={2}
              py={1}
              borderRadius="md"
              maxW="200px"
              isTruncated
            >
              {typeof dept === 'object' ? dept.name || 'Department' : dept}
            </Badge>
          </WrapItem>
        ))}
      </Wrap>
    );
  }

  // If it's a single object
  return (
    <Badge
      colorScheme="blue"
      variant="solid"
      fontSize="xs"
      px={2}
      py={1}
      borderRadius="md"
      maxW="200px"
      isTruncated
    >
      {typeof department === 'object'
        ? department.name || 'Department'
        : department}
    </Badge>
  );
};

const getDepartmentNames = (department) => {
  if (!department) return [];

  if (Array.isArray(department)) {
    return department.map((dept) =>
      typeof dept === 'object' ? dept.name || 'Department' : dept,
    );
  }

  if (typeof department === 'object') {
    return [department.name || 'Department'];
  }

  return [];
};

const ViewUserModal = ({ isOpen, onClose, viewUser }) => {
  const toast = useToast();
  const { t } = useTranslation();

  const modalBg = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const hoverBg = useColorModeValue('blue.50', 'gray.700');

  const emptyStateBg = useColorModeValue('blue.50', 'blue.900');
  const emptyStateBorderColor = useColorModeValue('blue.300', 'blue.700');

  const isAdmin = viewUser?.role === 'admin';
  const roleIcon = getRoleIcon(viewUser?.role);
  const roleColor = getRoleColor(viewUser?.role);
  const formattedRole = formatRole(viewUser?.role);

  // Get department info
  const departmentNames = getDepartmentNames(viewUser?.department);
  const departmentCount = departmentNames.length;

  const getStats = () => {
    const role = viewUser?.role;

    let showTalukas = false;
    let showFileTypes = false;

    if (role === 'talukaUser') {
      showTalukas = true;
    } else if (['projectOfficer', 'subDivisionalOfficer'].includes(role)) {
      showFileTypes = true;
    }

    return {
      departments: departmentCount,
      schemes: viewUser?.schemes?.length || 0,
      talukas: showTalukas ? viewUser?.tahsils?.length || 0 : 0,
      fileTypes: showFileTypes ? viewUser?.fileTypes?.length || 0 : 0,
      showTalukas,
      showFileTypes,
    };
  };

  const stats = getStats();
  const totalAssignments =
    stats.departments + stats.schemes + stats.talukas + stats.fileTypes;

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: 'Copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    });
  };

  const InfoItem = ({
    icon,
    label,
    value,
    copyable = false,
    tooltip = '',
    children,
  }) => (
    <Box
      p={3}
      borderRadius="lg"
      bg={hoverBg}
      _hover={{ bg: useColorModeValue('blue.100', 'gray.600') }}
    >
      <HStack spacing={2} align="center">
        <Icon as={icon} color={accentColor} boxSize={4} />
        <VStack align="start" spacing={0} flex={1}>
          <Text fontSize="xs" fontWeight="semibold" color={subtitleColor}>
            {label}
          </Text>
          {children || (
            <>
              {tooltip ? (
                <Tooltip label={tooltip} fontSize="xs">
                  <HStack spacing={1} cursor="help">
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      color={textColor}
                      isTruncated
                      maxW="200px"
                    >
                      {value || 'Not set'}
                    </Text>
                    {copyable && value && (
                      <Icon
                        as={CopyIcon}
                        boxSize={3}
                        color="blue.500"
                        cursor="pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy(value);
                        }}
                      />
                    )}
                  </HStack>
                </Tooltip>
              ) : (
                <HStack spacing={1}>
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color={textColor}
                    isTruncated
                    maxW="200px"
                  >
                    {value || 'Not set'}
                  </Text>
                  {copyable && value && (
                    <Icon
                      as={CopyIcon}
                      boxSize={3}
                      color="blue.500"
                      cursor="pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopy(value);
                      }}
                    />
                  )}
                </HStack>
              )}
            </>
          )}
        </VStack>
      </HStack>
    </Box>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      closeOnOverlayClick={false}
      scrollBehavior="inside"
    >
      <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.600" />
      <ModalContent
        rounded="2xl"
        shadow="2xl"
        overflow="hidden"
        borderColor={borderColor}
        maxH="90vh"
      >
        <Box
          bgGradient={`linear(to-r, ${roleColor}.500, ${roleColor}.700)`}
          py={6}
          px={8}
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top={0}
            right={0}
            bottom={0}
            left={0}
            bg="whiteAlpha.100"
            opacity={0.1}
          />

          <Flex
            direction={{ base: 'column', md: 'row' }}
            align={{ base: 'center', md: 'flex-start' }}
            justify="space-between"
            gap={6}
            position="relative"
            zIndex={1}
          >
            <HStack spacing={6}>
              <Avatar
                size="2xl"
                name={viewUser?.name || 'N/A'}
                bg={`${roleColor}.600`}
                color="white"
                border="4px solid"
                borderColor="white"
                boxShadow="lg"
              />

              <VStack align="start" spacing={2}>
                <Text fontSize="2xl" fontWeight="bold" color="white">
                  {capitalizeFirst(viewUser?.name)}
                </Text>

                <HStack spacing={3}>
                  <Tag
                    size="lg"
                    colorScheme={roleColor}
                    borderRadius="full"
                    px={4}
                    py={2}
                  >
                    <TagLeftIcon as={roleIcon} />
                    <TagLabel fontWeight="bold">{formattedRole}</TagLabel>
                  </Tag>

                  <Badge
                    colorScheme={viewUser?.active ? 'green' : 'red'}
                    variant="solid"
                    fontSize="sm"
                    px={3}
                    py={1}
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    <Icon
                      as={viewUser?.active ? FaCheckCircle : FaTimesCircle}
                      boxSize={3}
                    />
                    {viewUser?.active ? 'Active' : 'Inactive'}
                  </Badge>
                </HStack>

                <Text fontSize="sm" color="whiteAlpha.900">
                  User ID: {viewUser?.userId || 'N/A'}
                </Text>
              </VStack>
            </HStack>

            <Box textAlign={{ base: 'center', md: 'right' }}>
              <Stat>
                <StatLabel fontSize="xs" color="whiteAlpha.800">
                  Total Assignments
                </StatLabel>
                <StatNumber fontSize="3xl" color="white" fontWeight="bold">
                  {totalAssignments}
                </StatNumber>
                <StatHelpText fontSize="xs" color="whiteAlpha.800">
                  {stats.departments} department
                  {stats.departments !== 1 ? 's' : ''}
                  {stats.schemes > 0 && ` • ${stats.schemes} schemes`}
                  {stats.showTalukas &&
                    stats.talukas > 0 &&
                    ` • ${stats.talukas} talukas`}
                  {stats.showFileTypes &&
                    stats.fileTypes > 0 &&
                    ` • ${stats.fileTypes} file types`}
                </StatHelpText>
              </Stat>
            </Box>
          </Flex>
        </Box>

        <ModalCloseButton
          position="absolute"
          right={4}
          top={4}
          color="white"
          bg="blackAlpha.300"
          _hover={{ bg: 'blackAlpha.400' }}
          borderRadius="full"
        />

        <ModalBody
          bg={modalBg}
          px={8}
          py={6}
          maxH="calc(90vh - 180px)"
          overflowY="auto"
        >
          <Grid
            templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }}
            gap={4}
            mb={6}
          >
            <GridItem>
              <Card
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                boxShadow="sm"
              >
                <CardBody>
                  <VStack spacing={2}>
                    <Icon as={FaBuilding} color="teal.500" boxSize={6} />
                    <Stat>
                      <StatNumber fontSize="lg" color={textColor}>
                        {stats.departments}
                      </StatNumber>
                      <StatLabel fontSize="xs" color={subtitleColor}>
                        Department{stats.departments !== 1 ? 's' : ''}
                      </StatLabel>
                    </Stat>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem>
              <Card
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                boxShadow="sm"
              >
                <CardBody>
                  <VStack spacing={2}>
                    <Icon as={FaProjectDiagram} color="blue.500" boxSize={6} />
                    <Stat>
                      <StatNumber fontSize="lg" color={textColor}>
                        {stats.schemes}
                      </StatNumber>
                      <StatLabel fontSize="xs" color={subtitleColor}>
                        Schemes
                      </StatLabel>
                    </Stat>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>

            {stats.showTalukas && (
              <GridItem>
                <Card
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  boxShadow="sm"
                >
                  <CardBody>
                    <VStack spacing={2}>
                      <Icon as={FaMapMarkerAlt} color="green.500" boxSize={6} />
                      <Stat>
                        <StatNumber fontSize="lg" color={textColor}>
                          {stats.talukas}
                        </StatNumber>
                        <StatLabel fontSize="xs" color={subtitleColor}>
                          Talukas
                        </StatLabel>
                      </Stat>
                    </VStack>
                  </CardBody>
                </Card>
              </GridItem>
            )}

            {stats.showFileTypes && (
              <GridItem>
                <Card
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  boxShadow="sm"
                >
                  <CardBody>
                    <VStack spacing={2}>
                      <Icon as={FaFile} color="orange.500" boxSize={6} />
                      <Stat>
                        <StatNumber fontSize="lg" color={textColor}>
                          {stats.fileTypes}
                        </StatNumber>
                        <StatLabel fontSize="xs" color={subtitleColor}>
                          File Types
                        </StatLabel>
                      </Stat>
                    </VStack>
                  </CardBody>
                </Card>
              </GridItem>
            )}
          </Grid>

          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
            <VStack spacing={6} align="stretch">
              <Card
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                boxShadow="sm"
              >
                <CardHeader pb={3}>
                  <HStack spacing={2}>
                    <Icon as={FaUserAlt} color="blue.500" />
                    <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                      Basic Information
                    </Text>
                  </HStack>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack spacing={3} align="stretch">
                    <InfoItem
                      icon={FaIdBadge}
                      label="User ID"
                      value={viewUser?.userId}
                      copyable={true}
                      tooltip="Click the copy icon to copy"
                    />
                    <InfoItem
                      icon={PhoneIcon}
                      label="Phone Number"
                      value={viewUser?.phoneNo}
                      copyable={true}
                    />

                    <InfoItem
                      icon={FaBuilding}
                      label={`Department${
                        departmentCount !== 1 ? 's' : ''
                      } (${departmentCount})`}
                    >
                      {renderDepartmentData(viewUser?.department)}
                    </InfoItem>

                    <InfoItem icon={FaKey} label="Role" value={formattedRole} />
                    <InfoItem
                      icon={FaCalendar}
                      label="Account Status"
                      value={
                        viewUser?.active
                          ? 'Active (Verified)'
                          : 'Inactive (Suspended)'
                      }
                    />
                  </VStack>
                </CardBody>
              </Card>

              {!isAdmin &&
                (viewUser?.designation ||
                  viewUser?.nameOfOffice ||
                  (viewUser?.role === 'subDivisionalOfficer' &&
                    viewUser?.subDivision)) && (
                  <Card
                    bg={cardBg}
                    border="1px solid"
                    borderColor={borderColor}
                    boxShadow="sm"
                  >
                    <CardHeader pb={3}>
                      <HStack spacing={2}>
                        <Icon as={FaBuilding} color="orange.500" />
                        <Text
                          fontSize="lg"
                          fontWeight="semibold"
                          color={textColor}
                        >
                          Office Details
                        </Text>
                      </HStack>
                    </CardHeader>
                    <CardBody pt={0}>
                      <VStack spacing={3} align="stretch">
                        {viewUser?.role === 'subDivisionalOfficer' &&
                          viewUser?.subDivision && (
                            <InfoItem
                              icon={GiOfficeChair}
                              label="Sub-Division"
                              value={viewUser.subDivision.name}
                            />
                          )}
                        {viewUser?.designation && (
                          <InfoItem
                            icon={FaBriefcase}
                            label="Designation"
                            value={viewUser.designation}
                          />
                        )}
                        {viewUser?.nameOfOffice && (
                          <InfoItem
                            icon={FaBuilding}
                            label="Office Name"
                            value={viewUser.nameOfOffice}
                          />
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                )}
            </VStack>

            <VStack spacing={6} align="stretch">
              {!isAdmin && (
                <Card
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  boxShadow="sm"
                >
                  <CardHeader pb={3}>
                    <HStack spacing={2} justify="space-between">
                      <HStack spacing={2}>
                        <Icon as={FaProjectDiagram} color="blue.500" />
                        <Text
                          fontSize="lg"
                          fontWeight="semibold"
                          color={textColor}
                        >
                          Assigned Schemes ({stats.schemes})
                        </Text>
                      </HStack>
                    </HStack>
                  </CardHeader>
                  <CardBody pt={0}>
                    {stats.schemes > 0 ? (
                      <Box>{renderArrayData(viewUser.schemes, 'name')}</Box>
                    ) : (
                      <Text
                        fontSize="sm"
                        color={subtitleColor}
                        fontStyle="italic"
                      >
                        No schemes assigned
                      </Text>
                    )}
                  </CardBody>
                </Card>
              )}

              {viewUser?.role === 'talukaUser' && (
                <Card
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  boxShadow="sm"
                >
                  <CardHeader pb={3}>
                    <HStack spacing={2} justify="space-between">
                      <HStack spacing={2}>
                        <Icon as={FaMap} color="green.500" />
                        <Text
                          fontSize="lg"
                          fontWeight="semibold"
                          color={textColor}
                        >
                          Assigned Talukas ({stats.talukas})
                        </Text>
                      </HStack>
                    </HStack>
                  </CardHeader>
                  <CardBody pt={0}>
                    {stats.talukas > 0 ? (
                      <Box>{renderArrayData(viewUser.tahsils, 'name')}</Box>
                    ) : (
                      <Text
                        fontSize="sm"
                        color={subtitleColor}
                        fontStyle="italic"
                      >
                        No talukas assigned
                      </Text>
                    )}
                  </CardBody>
                </Card>
              )}

              {['projectOfficer', 'subDivisionalOfficer'].includes(
                viewUser?.role,
              ) && (
                <Card
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  boxShadow="sm"
                >
                  <CardHeader pb={3}>
                    <HStack spacing={2} justify="space-between">
                      <HStack spacing={2}>
                        <Icon as={FaFile} color="orange.500" />
                        <Text
                          fontSize="lg"
                          fontWeight="semibold"
                          color={textColor}
                        >
                          File Type Permissions ({stats.fileTypes})
                        </Text>
                      </HStack>
                    </HStack>
                  </CardHeader>
                  <CardBody pt={0}>
                    {stats.fileTypes > 0 ? (
                      <Box>{renderArrayData(viewUser.fileTypes, 'name')}</Box>
                    ) : (
                      <Text
                        fontSize="sm"
                        color={subtitleColor}
                        fontStyle="italic"
                      >
                        No file type permissions assigned
                      </Text>
                    )}
                  </CardBody>
                </Card>
              )}

              <Card
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                boxShadow="sm"
              >
                <CardBody>
                  <VStack spacing={3} align="stretch">
                    <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                      Assignment Summary
                    </Text>
                    <Progress
                      value={
                        (totalAssignments / Math.max(totalAssignments, 1)) * 100
                      }
                      colorScheme={roleColor}
                      size="sm"
                      borderRadius="full"
                      width={'100%'}
                    />
                    <SimpleGrid columns={4} spacing={4} mt={2}>
                      <VStack spacing={1}>
                        <Text fontSize="xs" color={subtitleColor}>
                          Departments
                        </Text>
                        <Text fontSize="lg" fontWeight="bold" color="teal.500">
                          {stats.departments}
                        </Text>
                      </VStack>

                      <VStack spacing={1}>
                        <Text fontSize="xs" color={subtitleColor}>
                          Schemes
                        </Text>
                        <Text fontSize="lg" fontWeight="bold" color="blue.500">
                          {stats.schemes}
                        </Text>
                      </VStack>

                      {viewUser?.role === 'talukaUser' && (
                        <VStack spacing={1}>
                          <Text fontSize="xs" color={subtitleColor}>
                            Talukas
                          </Text>
                          <Text
                            fontSize="lg"
                            fontWeight="bold"
                            color="green.500"
                          >
                            {stats.talukas}
                          </Text>
                        </VStack>
                      )}

                      {['projectOfficer', 'subDivisionalOfficer'].includes(
                        viewUser?.role,
                      ) && (
                        <VStack spacing={1}>
                          <Text fontSize="xs" color={subtitleColor}>
                            File Types
                          </Text>
                          <Text
                            fontSize="lg"
                            fontWeight="bold"
                            color="orange.500"
                          >
                            {stats.fileTypes}
                          </Text>
                        </VStack>
                      )}
                    </SimpleGrid>
                    <Text
                      fontSize="xs"
                      color={subtitleColor}
                      textAlign="center"
                    >
                      {totalAssignments} total assignments for{' '}
                      {formatRole(viewUser?.role)}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </Grid>

          {totalAssignments === 0 && !isAdmin && (
            <Card
              bg={emptyStateBg}
              border="2px dashed"
              borderColor={emptyStateBorderColor}
              mt={6}
            >
              <CardBody>
                <VStack spacing={3} align="center">
                  <Icon as={FaUserAlt} boxSize={10} color="blue.500" />
                  <VStack spacing={1}>
                    <Text fontWeight="semibold" color={textColor}>
                      No Assignments Yet
                    </Text>
                    <Text
                      fontSize="sm"
                      color={subtitleColor}
                      textAlign="center"
                    >
                      {viewUser?.role === 'talukaUser'
                        ? 'This Tehsildar has not been assigned any talukas or schemes.'
                        : viewUser?.role === 'projectOfficer' ||
                          viewUser?.role === 'subDivisionalOfficer'
                        ? 'This user has not been assigned any schemes or file type permissions.'
                        : 'This user has not been assigned any schemes.'}
                    </Text>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ViewUserModal;
